import { useEffect, useState } from 'react';
import { giveawayApi, twitchApi } from '../api';
import './Giveaway.css';

const GIVEAWAY_ROLE_ID = '1496560391071862795';

export default function Giveaway() {
  const [hasAccess, setHasAccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [giveaways, setGiveaways] = useState([]);
  const [selectedGiveaway, setSelectedGiveaway] = useState(null);
  const [entries, setEntries] = useState([]);
  const [entriesLoading, setEntriesLoading] = useState(false);
  const [winner, setWinner] = useState(null);
  const [drawingWinner, setDrawingWinner] = useState(false);
  const [word, setWord] = useState('');
  const [botCommand, setBotCommand] = useState('');
  const [botConnected, setBotConnected] = useState(false);
  const [twitchUsername, setTwitchUsername] = useState('');
  const [sending, setSending] = useState(false);
  const [creating, setCreating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    let cancelled = false;

    const token = localStorage.getItem('discord_token');
    if (!token) {
      setHasAccess(false);
      setLoading(false);
      return;
    }

    fetch(`${process.env.REACT_APP_API_BASE || '/api'}/user-data`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(res => res.json())
      .then(data => {
        if (cancelled) return;
        setHasAccess(Array.isArray(data.roles) && data.roles.includes(GIVEAWAY_ROLE_ID));
      })
      .catch(() => {
        if (!cancelled) setHasAccess(false);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!hasAccess) return;
    let cancelled = false;
    giveawayApi.list()
      .then(data => { if (!cancelled) setGiveaways(data); })
      .catch(() => { if (!cancelled) setGiveaways([]); });
    return () => { cancelled = true; };
  }, [hasAccess]);

  useEffect(() => {
    const token = localStorage.getItem('twitch_token');
    const username = localStorage.getItem('twitch_username');
    setBotConnected(!!token);
    setTwitchUsername(username || '');
  }, []);

  const loadEntries = async (id) => {
    setEntriesLoading(true);
    try {
      const data = await giveawayApi.getById(id);
      setSelectedGiveaway(data);
      setEntries(data.entries || []);
      setWinner(data.winner_id || null);
    } catch (e) {
      setError('Fehler beim Laden der Teilnehmer.');
    } finally {
      setEntriesLoading(false);
    }
  };

  const handleDrawWinner = async (id) => {
    setDrawingWinner(true);
    setError('');
    try {
      const result = await giveawayApi.drawWinner(id);
      setWinner(result.winner);
      setSuccess(`Gewinner: ${result.winner.display_name || result.winner.username}!`);
      const list = await giveawayApi.list();
      setGiveaways(list);
    } catch (e) {
      setError(e?.response?.data?.error === 'no_entries'
        ? 'Keine Teilnehmer vorhanden.'
        : 'Fehler beim Ziehen des Gewinners.');
    } finally {
      setDrawingWinner(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!twitchUsername) {
      setError('Bitte verbinde zuerst deinen Twitch-Account.');
      return;
    }
    setCreating(true);
    setError('');
    setSuccess('');
    try {
      const result = await giveawayApi.create({ 
        word, 
        streamer_name: twitchUsername,
        channel: twitchUsername 
      });
      setSuccess(`Giveaway gestartet! Wort: ${result.word}`);
      setWord('');
      const list = await giveawayApi.list();
      setGiveaways(list);
    } catch (err) {
      setError(err?.response?.data?.error || 'Erstellung fehlgeschlagen');
    } finally {
      setCreating(false);
    }
  };

  const handleEnd = async (id) => {
    try {
      await giveawayApi.end(id);
      const list = await giveawayApi.list();
      setGiveaways(list);
      if (selectedGiveaway?.id === id) {
        setSelectedGiveaway(null);
        setEntries([]);
        setWinner(null);
      }
      setSuccess('Giveaway beendet.');
    } catch (e) {
      const errMsg = e?.response?.data?.error;
      if (errMsg === 'not_found_or_ended') {
        setError('Giveaway ist bereits beendet.');
      } else {
        setError('Fehler beim Beenden.');
      }
    }
  };

  const handleSendCommand = async (e) => {
    e.preventDefault();
    if (!botCommand.trim() || sending) return;
    if (!twitchUsername) {
      setError('Bitte verbinde zuerst deinen Twitch-Account.');
      return;
    }
    setSending(true);
    setError('');
    try {
      await twitchApi.say(twitchUsername, botCommand);
      setSuccess(`Command gesendet: ${botCommand}`);
      setBotCommand('');
    } catch (err) {
      setError(err?.response?.data?.error || 'Command konnte nicht gesendet werden.');
    } finally {
      setSending(false);
    }
  };

  const handleCopyCommand = () => {
    navigator.clipboard.writeText('/mod salernobot').then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {
      const textarea = document.createElement('textarea');
      textarea.value = '/mod salernobot';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  if (loading) {
    return (
      <div className="page giveaway-page">
        <div className="loading-container">
          <div className="spinner" />
          <p className="muted">Lädt...</p>
        </div>
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div className="page giveaway-page">
        <h1>🎁 Giveaway</h1>
        <div className="giveaway-restricted">
          <p>Du hast keinen Zugriff auf dieses Feature.</p>
          <p className="muted">Dieses Feature ist nur für autorisierte User verfügbar.</p>
        </div>
      </div>
    );
  }

  const getStatusLabel = (status) => {
    switch (status) {
      case 'active': return 'Aktiv';
      case 'ended': return 'Beendet';
      case 'winner_drawn': return 'Gelöst';
      default: return status;
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'active': return 'giveaway-status-active';
      case 'ended': return 'giveaway-status-ended';
      case 'winner_drawn': return 'giveaway-status-winner';
      default: return '';
    }
  };

  return (
    <div className="page giveaway-page">
      <h1>🎁 Giveaway</h1>

      {error && <p className="giveaway-error">{error}</p>}
      {success && <p className="giveaway-success">{success}</p>}

      <div className="giveaway-card">
        <h2>Neues Giveaway erstellen</h2>
        <p className="giveaway-info-text">
          ℹ️ <strong>Voraussetzung:</strong> SalernoBot muss als Moderator/Mod im Twitch-Channel eingetragen sein.
          Das Giveaway-Wort wird im Chat überwacht — wer es schreibt, wird automatisch eingetragen.
        </p>
        <div className="giveaway-command">
          <code>/mod salernobot</code>
          <button className="giveaway-btn-copy" onClick={handleCopyCommand}>
            {copied ? '✅ Kopiert!' : '📋 Kopieren'}
          </button>
        </div>
        {botConnected ? (
          <div className="giveaway-bot-status giveaway-bot-online">
            <span className="giveaway-bot-dot" />
            SalernoBot verbunden
          </div>
        ) : (
          <div className="giveaway-bot-status giveaway-bot-offline">
            <span className="giveaway-bot-dot" />
            SalernoBot nicht verbunden
          </div>
        )}
        <form onSubmit={handleCreate} className="giveaway-form">
          <div className="giveaway-form-row">
            <input
              type="text"
              value={word}
              onChange={(e) => setWord(e.target.value)}
              placeholder="Giveaway-Wort"
              className="giveaway-input"
              required
            />
          </div>
          <button type="submit" disabled={creating} className="giveaway-btn-create">
            {creating ? 'Erstelle...' : '🎁 Giveaway starten'}
          </button>
        </form>
      </div>

      <div className="giveaway-card">
        <h2>💬 Command an Twitch senden</h2>
        <p className="giveaway-info-text">
          Sende einen Befehl direkt an den Twitch-Chat über SalernoBot.
        </p>
        <form onSubmit={handleSendCommand} className="giveaway-form">
          <div className="giveaway-form-row">
            <input
              type="text"
              value={botCommand}
              onChange={(e) => setBotCommand(e.target.value)}
              placeholder="z.B. !giveaway start oder Connected"
              className="giveaway-input"
              disabled={!botConnected}
            />
            <button
              type="submit"
              disabled={sending || !botCommand.trim() || !botConnected}
              className="giveaway-btn-create"
              style={{ flex: 'unset', whiteSpace: 'nowrap' }}
            >
              {sending ? 'Sende...' : '📤 Senden'}
            </button>
          </div>
          {!botConnected && (
            <p className="muted" style={{ fontSize: '0.8rem' }}>
              ⚠️ Verbinde zuerst deinen Twitch-Account um Befehle zu senden.
            </p>
          )}
        </form>
      </div>

      <div className="giveaway-card">
        <h2>Aktive Giveaways</h2>
        {giveaways.length === 0 ? (
          <p className="muted">Keine aktiven Giveaways.</p>
        ) : (
          <div className="giveaway-list">
            {giveaways.map((g) => (
              <div
                key={g.id}
                className={`giveaway-item ${selectedGiveaway?.id === g.id ? 'giveaway-item-active' : ''}`}
                onClick={() => loadEntries(g.id)}
              >
                <div className="giveaway-item-info">
                  <span className="giveaway-item-name">{g.streamer_name}</span>
                  <span className="giveaway-item-channel">#{g.channel}</span>
                  <span className="giveaway-item-word">Wort: {g.word}</span>
                  <span className={`giveaway-status-badge ${getStatusClass(g.status)}`}>
                    {getStatusLabel(g.status)}
                  </span>
                </div>
                <div className="giveaway-item-meta">
                  <span className="giveaway-item-count">{g.entry_count || 0} Teilnehmer</span>
                  {g.status === 'active' && (
                    <button
                      className="giveaway-btn-end"
                      onClick={(e) => { e.stopPropagation(); handleEnd(g.id); }}
                    >
                      Beenden
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {selectedGiveaway && (
        <div className="giveaway-card">
          <div className="giveaway-entries-header">
            <h2>
              Teilnehmer — <span className="giveaway-entries-channel">#{selectedGiveaway.channel}</span>
            </h2>
            <div className="giveaway-entries-header-right">
              <span className="giveaway-entries-word">{selectedGiveaway.word}</span>
              <span className={`giveaway-status-badge ${getStatusClass(selectedGiveaway.status)}`}>
                {getStatusLabel(selectedGiveaway.status)}
              </span>
            </div>
          </div>
          {winner && (
            <div className="giveaway-winner">
              🏆 <strong>{winner.display_name || winner.username}</strong> hat gewonnen!
            </div>
          )}
          {entries.length > 0 && (
            <button
              className="giveaway-btn-draw"
              onClick={() => handleDrawWinner(selectedGiveaway.id)}
              disabled={drawingWinner || selectedGiveaway.status !== 'active'}
            >
              {drawingWinner ? 'Ziehe...' : '🏆 Gewinner ziehen'}
            </button>
          )}
          {entriesLoading ? (
            <div className="loading-container">
              <div className="spinner" />
            </div>
          ) : entries.length === 0 ? (
            <p className="muted">Noch keine Teilnehmer.</p>
          ) : (
            <div className="giveaway-entries-list">
              {entries.map((entry, idx) => (
                <div
                  key={entry.id}
                  className={`giveaway-entry-item ${winner?.id === entry.id ? 'giveaway-entry-winner' : ''}`}
                >
                  <span className="giveaway-entry-rank">#{idx + 1}</span>
                  <span className="giveaway-entry-name">{entry.display_name || entry.username}</span>
                  {winner?.id === entry.id && <span className="giveaway-entry-badge">🏆 Gewinner</span>}
                  <span className="giveaway-entry-time">
                    {new Date(entry.created_at).toLocaleString('de-DE')}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
